import React, { useContext, useEffect, useState } from "react";
import BarChart from "../charts/BarChart";
import PieChart from "../charts/PieChart";
import ActivityQuestions from "./ActivityQuestions";
import ListChartModal from "./ListChartModal";
import { CirclePlus, Trash2 } from "lucide-react";
import { toast } from "../../../utils/toast";
import DeletePermission from "../../suspence/DeletePermission";
import AppContext from "../../../context/AppContext";
import AreaChart from "../charts/AreaChart";
import RadarChart from "../charts/RadarChart";
import {
  fetchDashboardData,
  createGraphMetric,
  deleteDashboard,
  deleteGraphMetric,
} from "../../../service/UserDashboard";
import translations from "../../common/Translations";
import CommonButton from "../../common/CommonButton";
import { Spinner } from "../../../Spinner";

const AddChart = ({ dashboardNameInput, setDashboardNameInput, roleInput, dashboardPerm, setDashboardActions }) => {
  const { theme, langMode } = useContext(AppContext);
  const [isListChartModalOpen, setIsListChartModalOpen] = useState(false);
  const [openActivityQueModal, setOpenActivityQueModal] = useState(false);
  const [tempMapData, setTempMapData] = useState([]);
  const [selectedFieldQuestionId, setSelectedFieldQuestionId] = useState("");
  const [roleForDelete, setRoleForDelete] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemType, setDeleteItemType] = useState("");
  const [deleteItemId, setDeleteItemId] = useState("");
  const [selectedChart, setSelectedChart] = useState();
  const [loading, setLoading] = useState(false); // New loading state
  const getBackendMessage = (payload) =>
    payload?.body?.value ??
    payload?.body?.error ??
    payload?.header?.message ??
    payload?.message;

  const showDeleteModal = (type, id) => {
    setDeleteItemType(type);
    setDeleteItemId(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleCloseListModal = () => {
    setIsListChartModalOpen(false);
  };

  const handleCloseQuestionsModal = () => {
    setOpenActivityQueModal(false);
  };

  const handleOpenListModal = () => {
    if (selectedFieldQuestionId) {
      setIsListChartModalOpen(true);
      setOpenActivityQueModal(false);
    } else {
      toast.warn(translations[langMode].selectFieldWarning); // Use translated warning
    }
  };

  const handleSelectedChart = (chart) => {
    setSelectedChart(chart);
  };

  const handleQuestionChange = (event) => {
    setSelectedFieldQuestionId(event.target.value);
  };

  useEffect(() => {
    if (dashboardNameInput) {
      fetchData();
    }
  }, [dashboardNameInput, roleInput]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const responseData = await fetchDashboardData(
        dashboardNameInput,
        roleInput
      );
      if (responseData?.header?.code === 600) {
        const dashboardData = responseData?.body?.value;
        const graphList = Array.isArray(dashboardData?.graphList)
          ? dashboardData.graphList
          : [];
        setTempMapData(graphList);
        setRoleForDelete(dashboardData?.role ?? "");
      } else {
        const backendMessage = getBackendMessage(responseData);
        if (backendMessage) {
          toast.error(backendMessage);
        }
        setTempMapData([]);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
      setTempMapData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGraph = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await createGraphMetric(
        dashboardNameInput,
        selectedChart,
        selectedFieldQuestionId,
        roleInput
      );
      if (response.header.code === 600) {
        toast.success(response.body.value);
        handleCloseListModal();
        fetchData();
      } else {
        const backendMessage = getBackendMessage(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Failed to create graph:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDashboard = async () => {
    setLoading(true);
    try {
      const response = await deleteDashboard(dashboardNameInput, roleInput);
      if (response.header.code === 600) {
        toast.success(response.body.value);
        handleCloseDeleteModal();
        setDashboardNameInput("");
      } else {
        const backendMessage = getBackendMessage(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Failed to delete dashboard:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGraphMetric = async (graphMetricId) => {
    setLoading(true);
    try {
      const response = await deleteGraphMetric(graphMetricId, roleForDelete);
      if (response.header.code === 600) {
        toast.success(response.body.value);
        handleCloseDeleteModal();
        fetchData();
      } else {
        const backendMessage = getBackendMessage(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Failed to delete graph metric:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Render the delete-dashboard action into the toolbar row above (next to the
  // dashboard name select) instead of its own row here, and clear it whenever
  // there's no dashboard selected or this component unmounts.
  useEffect(() => {
    if (!setDashboardActions) return undefined;

    if (!dashboardNameInput) {
      setDashboardActions(null);
      return undefined;
    }

    setDashboardActions(
      <CommonButton colorIntent="danger"
        tooltipText={translations[langMode].deletethedashboard}
        tooltipPosition="top"
        onClick={() => showDeleteModal("dashboard", dashboardNameInput)}
        variant="danger"
        disabled={!dashboardPerm?.canDelete}
        className={`${!dashboardPerm?.canDelete ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <Trash2 className="h-4 w-4" /> {translations[langMode].deletDashbord}
      </CommonButton>
    );

    return () => setDashboardActions(null);
  }, [setDashboardActions, dashboardNameInput, dashboardPerm?.canDelete, langMode]);

  return (
    <>
      {dashboardNameInput && (
        <div
          className={`mt-6 w-full rounded-lg transition-colors duration-300
            ${theme === "dark"
              ? "bg-gray-900 text-gray-100 border border-gray-700"
              : theme === "high-contrast"
                ? "bg-black text-white border-2 border-yellow-400"
                : "text-gray-800"
            }`}
        >
          {loading && <Spinner />}

          {tempMapData.length === 0 ? (
            <div className="flex items-center justify-center px-4 py-16">
              <div
                className={`flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all duration-300 ease-in-out
                  ${theme === "dark"
                    ? "bg-gray-800 border-[#236DB4]/40 shadow-lg shadow-black/30"
                    : theme === "high-contrast"
                      ? "bg-black border-yellow-400"
                      : "bg-white border-[#236DB4]/25 shadow-md"
                  }`}
              >
                <span
                  className={`flex h-16 w-16 items-center justify-center rounded-full
                    ${theme === "dark"
                      ? "bg-[#236DB4]/20 text-[#236DB4]"
                      : theme === "high-contrast"
                        ? "bg-black text-yellow-400"
                        : "bg-[#236DB4]/10 text-[#236DB4]"
                    }`}
                >
                  <CirclePlus className="h-8 w-8" />
                </span>
                <div className="flex flex-col gap-1">
                  <span
                    className={`text-base font-semibold
                      ${theme === "dark"
                        ? "text-gray-100"
                        : theme === "high-contrast"
                          ? "text-yellow-400"
                          : "text-gray-900"
                      }`}
                  >
                    {translations[langMode].noChartsTitle}
                  </span>
                  <span
                    className={`text-sm
                      ${theme === "dark"
                        ? "text-gray-400"
                        : theme === "high-contrast"
                          ? "text-yellow-200"
                          : "text-gray-500"
                      }`}
                  >
                    {translations[langMode].noChartsSubtitle}
                  </span>
                </div>
                <CommonButton colorIntent="primary"
                  onClick={() => setOpenActivityQueModal(true)}
                  variant="primary"
                  className="mt-2"
                  aria-label={translations[langMode].AddChart}
                  disabled={!dashboardPerm?.canCreate}
                >
                  <CirclePlus className="h-4 w-4" />
                  {translations[langMode].AddChart}
                </CommonButton>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 w-full px-2 pb-4">
              {tempMapData.map((data, i) => (
                <div
                  key={i}
                  className={`relative p-5 rounded-xl shadow-lg transition-all duration-300 ease-in-out
                    ${theme === "dark"
                      ? "bg-gray-800 border border-gray-700"
                      : theme === "high-contrast"
                        ? "bg-black border-2 border-yellow-400 text-white"
                        : "bg-white border border-gray-200"
                    }`}
                >
                  <button
                    className={`absolute top-5 right-10 z-10 inline-flex h-6 w-6 items-center justify-center text-red-500 hover:text-red-700 transition-colors duration-200 rounded-full bg-white bg-opacity-70 dark:bg-gray-700 dark:bg-opacity-70 ${dashboardPerm?.canDelete ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                    onClick={() => showDeleteModal("graph", data.graphMetricId)}
                    aria-label={`Delete chart ${data.graphValueList[0]?.question || "No Name"}`}
                    disabled={!dashboardPerm?.canDelete}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="w-full h-full">
                    {data.graphType === "graph" && (
                      <BarChart
                        tempData={data}
                        chartName={data.graphValueList[0]?.question || "No Name"}
                      />
                    )}
                    {data.graphType === "chart" && (
                      <PieChart
                        tempData={data}
                        chartName={data.graphValueList[0]?.question || "No Name"}
                      />
                    )}
                    {data.graphType === "area" && (
                      <AreaChart
                        tempData={data}
                        chartName={data.graphValueList[0]?.question || "No Name"}
                      />
                    )}
                    {data.graphType === "radar" && (
                      <RadarChart
                        tempData={data}
                        chartName={data.graphValueList[0]?.question || "No Name"}
                      />
                    )}
                  </div>
                </div>
              ))}
              <div className="flex h-full min-h-[300px]">
                <button
                  onClick={() => setOpenActivityQueModal(true)}
                  className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl text-center
                  h-full w-full px-6
                  shadow-md hover:shadow-xl transition-all duration-300 ease-in-out
                  ${dashboardPerm?.canCreate ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                  ${theme === "dark"
                      ? "bg-gray-800 border-[#236DB4]/50 hover:bg-gray-700"
                      : theme === "high-contrast"
                        ? "bg-black border-yellow-400 text-yellow-400 hover:bg-gray-900"
                        : "bg-white border-[#236DB4]/40 hover:bg-[#236DB4]/5"
                    }`}
                  aria-label={translations[langMode].AddChart}
                  disabled={!dashboardPerm?.canCreate}
                >
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-full
                      ${theme === "dark"
                        ? "bg-[#236DB4]/20 text-[#236DB4]"
                        : theme === "high-contrast"
                          ? "bg-black text-yellow-400"
                          : "bg-[#236DB4]/10 text-[#236DB4]"
                      }`}
                  >
                    <CirclePlus className="h-7 w-7" />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span
                      className={`text-base font-semibold ${theme === "dark"
                        ? "text-gray-100"
                        : theme === "high-contrast"
                          ? "text-yellow-400"
                          : "text-[#236DB4]"
                        }`}
                    >
                      {translations[langMode].AddChart}
                    </span>
                    <span
                      className={`text-sm ${theme === "dark"
                        ? "text-gray-400"
                        : theme === "high-contrast"
                          ? "text-yellow-200"
                          : "text-gray-500"
                        }`}
                    >
                      {translations[langMode].addChartCardSubtitle}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isListChartModalOpen && (
        <ListChartModal
          onClose={handleCloseListModal}
          handleSelectedChart={handleSelectedChart}
          handleCreateGraph={handleCreateGraph}
        />
      )}
      {openActivityQueModal && (
        <ActivityQuestions
          onClose={handleCloseQuestionsModal}
          handleOpenListModal={handleOpenListModal}
          dashboardNameInput={dashboardNameInput}
          selectedFieldQuestionId={selectedFieldQuestionId}
          setSelectedFieldQuestionId={setSelectedFieldQuestionId}
          handleQuestionChange={handleQuestionChange}
        />
      )}
      {deleteModalOpen && deleteItemType === "dashboard" && (
        <DeletePermission
          deleteFunction={handleDeleteDashboard}
          onClose={handleCloseDeleteModal}
          onDeleteText={translations[langMode].DeleteDash}
        />
      )}
      {deleteModalOpen && deleteItemType === "graph" && (
        <DeletePermission
          deleteFunction={() =>
            handleDeleteGraphMetric(deleteItemId, roleInput)
          }
          onClose={handleCloseDeleteModal}
          onDeleteText={translations[langMode].DeleteGraph}
        />
      )}
    </>
  );
};

export default AddChart;
